/* ============================================================================
   ANCONA APEX V5 — PRODUCT ROUTE MODULE (ancona-v5-product.js)
   Agent E. The ONLY custom script on product templates (classic script, defer,
   emitted once from layout/theme.liquid). Replaces the PDP behavior of the
   retired ancona-v4-product.js + the sticky-ATC segment of ancona-v4-core.js,
   rewritten lean.

   Owns:
   1. Exact-variant continuity: keeps [data-av5-product-context] (JSON) and the
      Client Book toggle's data-av5-* attributes in sync with the selected
      public variant via Dawn's 'variant-change' pubsub event. Dawn's own
      product-info / product-form / cart drawer remain the commerce authority.
   2. Sticky mobile ATC contract: show only while the real form is off-screen;
      proxy click to the real submit button; mirror disabled/label/price;
      footer guard attribute. (Dialog suppression + hidden state are pure CSS.)
   3. Variant-picker keyboard continuity: after Dawn swaps the picker DOM on a
      change, focus is restored to the same radio so arrow-key navigation
      keeps working end to end (P0-2).
   4. Sold-out recovery visibility for the exact selected variant.
   5. "Print details" reveal + native window.print.

   Progressive-enhancement pattern (P0-8): idempotent init on DOMContentLoaded
   and shopify:section:load; per-element initialized guards; no global
   MutationObserver rescanning the document. Every feature no-ops when its
   markup is absent, so this file is inert off the PDP.
   ============================================================================ */
(function () {
  'use strict';
  if (window.__anconaV5Product) return;
  window.__anconaV5Product = true;

  var VARIANT_EVENT = 'variant-change'; /* Dawn PUB_SUB_EVENTS.variantChange */

  function q(sel, root) {
    try { return (root || document).querySelector(sel); } catch (e) { return null; }
  }

  function sectionRoot(sectionId) {
    if (!sectionId) return null;
    return q('product-info[data-section="' + sectionId + '"]');
  }

  /* The settled, Dawn-rendered price text for a section (publish fires AFTER
     Dawn rewrites the price node, so a synchronous read is already fresh). */
  function renderedPrice(root) {
    var scope = root ? root.querySelector('[id^="price-"]') : q('[id^="price-"]');
    if (!scope) return '';
    var el = scope.querySelector('.price-item--sale:not([hidden])') ||
      scope.querySelector('.price-item--regular:not([hidden])') ||
      scope.querySelector('.price-item--regular') ||
      scope.querySelector('.price-item');
    var t = el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : '';
    return t.slice(0, 32);
  }

  function realAtcButton(root) {
    if (root) return root.querySelector('product-form button[name="add"]');
    return q('product-form button[name="add"]') || q('button[name="add"]');
  }

  /* ---- 1) Product context + Client Book attribute continuity -------------- */
  function syncContext(root, variant) {
    var node = root ? root.querySelector('[data-av5-product-context]') : q('[data-av5-product-context]');
    if (!node) return;
    var ctx;
    try { ctx = JSON.parse(node.textContent || '{}'); } catch (e) { ctx = null; }
    if (!ctx || typeof ctx !== 'object') return;

    var price = renderedPrice(root) || ctx.price || '';
    var base = String(ctx.url || '').split('?')[0].split('#')[0];
    var hasVariant = !!(variant && variant.id != null);
    var vid = hasVariant ? String(variant.id) : '';
    var image = ctx.image || '';
    try {
      if (variant && variant.featured_media && variant.featured_media.preview_image && variant.featured_media.preview_image.src) {
        image = variant.featured_media.preview_image.src;
      }
    } catch (e) { /* keep prior image */ }

    ctx.variantId = vid;
    ctx.variantTitle = hasVariant && typeof variant.title === 'string' ? variant.title : '';
    ctx.url = vid ? base + '?variant=' + vid : base;
    if (price) ctx.price = price;
    ctx.image = image;
    try { node.textContent = JSON.stringify(ctx); } catch (e) { /* leave as-is */ }

    /* Mirror onto the Client Book toggle so a save captures the exact variant. */
    var btn = (root && root.querySelector('[data-av5-book]')) || null;
    if (btn) {
      btn.setAttribute('data-av5-variant-id', vid);
      btn.setAttribute('data-av5-url', ctx.url);
      btn.setAttribute('aria-disabled', hasVariant ? 'false' : 'true');
      if (hasVariant) btn.removeAttribute('data-av5-invalid-variant');
      else btn.setAttribute('data-av5-invalid-variant', '');
      if (price) btn.setAttribute('data-av5-price', price);
      if (image) btn.setAttribute('data-av5-image', image);
    }

    /*
     * The Client Book module is interaction-loaded.  This event lets it
     * repaint save state when present without coupling the product route to a
     * particular module implementation; when absent it is a harmless no-op.
     */
    try {
      document.dispatchEvent(new CustomEvent('ancona:v5:variant-context', {
        detail: { sectionId: root && root.dataset ? root.dataset.section : '', context: ctx }
      }));
    } catch (e) { /* CustomEvent unavailable: data attributes still update */ }
  }

  /* ---- 2) Sticky mobile ATC ----------------------------------------------- */
  function initStickyBar(bar) {
    if (bar.dataset.initialized === 'true') return;
    bar.dataset.initialized = 'true';

    var proxy = bar.querySelector('.ancona-sticky-atc__btn');
    var owner = bar.closest('product-info');
    var anchorSelector = bar.getAttribute('data-watch') || 'product-form';
    var anchor = owner ? q(anchorSelector, owner) : q(anchorSelector);

    if (proxy) {
      proxy.addEventListener('click', function () {
        var real = realAtcButton(bar.closest('product-info'));
        if (!real || real.disabled || real.getAttribute('aria-disabled') === 'true' || proxy.disabled) return;
        real.click(); /* native Dawn AJAX add + cart drawer */
      });
    }

    function setVisible(on) {
      bar.setAttribute('data-visible', on ? 'true' : 'false');
      bar.setAttribute('aria-hidden', on ? 'false' : 'true');
      try { bar.inert = !on; } catch (e) { /* visibility:hidden still guards */ }
    }
    setVisible(false);

    if (anchor && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        setVisible(!entries[0].isIntersecting);
      }, { rootMargin: '0px 0px -20% 0px' }).observe(anchor);
    }

    /* Footer guard: never cover the page footer (CSS slides the bar away). */
    var pageFooter = q('footer.footer');
    if (pageFooter && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        bar.setAttribute('data-ancona-footer-visible', entries[0].isIntersecting ? 'true' : 'false');
      }).observe(pageFooter);
    }

    /* Truth mirror for the setUnavailable path (Dawn publishes no variant
       event for an unavailable option combination): a SCOPED observer on the
       one real submit button, never a document-wide rescan. */
    var real = realAtcButton(bar.closest('product-info'));
    if (real && typeof MutationObserver === 'function') {
      try {
        new MutationObserver(function () { mirrorSticky(bar); }).observe(real, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
          childList: true,
          subtree: true,
          characterData: true
        });
      } catch (e) { /* best effort */ }
    }
  }

  function mirrorSticky(bar, root) {
    bar = bar || q('[data-av5-sticky-atc]');
    if (!bar) return;
    var real = realAtcButton(root || bar.closest('product-info'));
    var proxy = bar.querySelector('.ancona-sticky-atc__btn');
    if (!real || !proxy) return;
    var off = !!real.disabled || real.getAttribute('aria-disabled') === 'true';
    proxy.disabled = off;
    if (off) proxy.setAttribute('aria-disabled', 'true');
    else proxy.removeAttribute('aria-disabled');
    var span = real.querySelector('span');
    var label = ((span ? span.textContent : real.textContent) || '').replace(/\s+/g, ' ').trim();
    if (label) proxy.textContent = label;
    var price = renderedPrice(root || bar.closest('product-info'));
    var priceEl = bar.querySelector('[data-ancona-price]');
    if (priceEl && price) priceEl.textContent = price;
  }

  /* ---- 3) Variant-picker keyboard continuity ------------------------------ */
  var lastRadioId = null;
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (t && t.type === 'radio' && t.closest && t.closest('variant-selects') && t.id) {
      lastRadioId = t.id;
    }
  }, true);

  function restorePickerFocus() {
    if (!lastRadioId) return;
    var id = lastRadioId;
    window.requestAnimationFrame(function () {
      var active = document.activeElement;
      /* Only restore when Dawn's DOM swap dropped focus (or parked it on a
         node being removed) — never steal focus the user moved elsewhere. */
      var lost = !active || active === document.body ||
        (active.closest && !document.contains(active)) ||
        (active.id && active.id.indexOf(id + '-') === 0);
      var fresh = document.getElementById(id);
      if (lost && fresh && fresh.type === 'radio') {
        try { fresh.focus({ preventScroll: true }); } catch (e) { try { fresh.focus(); } catch (e2) {} }
      }
    });
  }

  /* ---- 4) Exact-variant sold-out recovery --------------------------------- */
  function syncSoldout(root, variant) {
    var box = root ? root.querySelector('[data-ancona-soldout-variant]') : q('[data-ancona-soldout-variant]');
    if (!box) return;
    var soldOut = !!(variant && variant.available === false);
    if (!variant) {
      /* No exact variant proof (e.g. unavailable combination) — never claim a
         sellout without evidence. */
      soldOut = false;
    }
    box.hidden = !soldOut;
  }

  /* ---- 5) Print details (native, no library) ------------------------------ */
  function initPrint(root) {
    var btn = (root || document).querySelector('#AnconaPrintBtn');
    if (!btn) return;
    var wrap = btn.closest('.ancona-print');
    if (wrap) wrap.hidden = false;
    if (btn.dataset.initialized === 'true') return;
    btn.dataset.initialized = 'true';
    btn.addEventListener('click', function () { window.print(); });
  }

  /* ---- variant-change subscription ---------------------------------------- */
  function onVariantChange(event) {
    try {
      var data = event && event.data ? event.data : {};
      if (data.instanceSectionId && data.instanceSectionId !== data.sectionId) return;
      var root = sectionRoot(data.sectionId);
      if (!root || !root.querySelector('[data-av5-product-context]')) return;
      syncContext(root, data.variant || null);
      mirrorSticky(null, root);
      syncSoldout(root, data.variant || null);
      restorePickerFocus();
    } catch (e) { /* never break the PDP */ }
  }

  function onVariantUnavailable(event) {
    try {
      var detail = event && event.detail ? event.detail : {};
      if (detail.instanceSectionId && detail.instanceSectionId !== detail.sectionId) return;
      var root = sectionRoot(detail.sectionId);
      if (!root || !root.querySelector('[data-av5-product-context]')) return;
      syncContext(root, null);
      mirrorSticky(null, root);
      syncSoldout(root, null);
      restorePickerFocus();
    } catch (e) { /* never break the PDP */ }
  }

  /* ---- boot (P0-8 pattern) ------------------------------------------------- */
  function init(root) {
    root = root || document;
    if (!q('product-info') && !root.querySelector('[data-av5-sticky-atc]')) return; /* off-PDP: inert */
    root.querySelectorAll('[data-av5-sticky-atc]').forEach(function (bar) {
      initStickyBar(bar);
      mirrorSticky(bar, bar.closest('product-info'));
    });
    initPrint(root);
  }

  function boot() {
    init(document);
    if (typeof window.subscribe === 'function') {
      try { window.subscribe(VARIANT_EVENT, onVariantChange); } catch (e) { /* pubsub absent */ }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
  document.addEventListener('ancona:v5:product-swapped', function (event) {
    var root = event.detail && event.detail.root;
    if (root && root.querySelectorAll) init(root);
  });
  document.addEventListener('ancona:v5:variant-unavailable', onVariantUnavailable);
})();
